import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Check, ArrowLeft, Coffee } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import MaskedReveal from "@/components/motion/MaskedReveal";
import Reveal from "@/components/motion/Reveal";
import HexMark from "@/components/motion/HexMark";
import Seo from "@/components/Seo";
import { CONTACT_FORM, PROPOSAL } from "@/constants/testIds";

const SERVICE_OPTIONS = [
  "Sourcing",
  "Project Management",
  "Logistics Planning",
  "Concierge (Hourly)",
  "Concierge (Full-Service)",
  "Audio-Visual Production",
  "On-Site Execution",
  "Registration Platform",
  "Mobile App",
  "Virtual / Hybrid",
  "Sponsorship Management",
  "Marketing & Branding",
  "Web Design",
];

// ------------------------------
// TidyCal Booking Embed (replaces the short contact form)
// React SPA-safe: fully re-injects the script on mount so the embed's
// DOM scan always finds our fresh .tidycal-embed div. Prevents the
// "TidyCal.init(): element not provided" error on route re-visits.
// ------------------------------
function ContactForm() {
  useEffect(() => {
    const SCRIPT_SRC = "https://asset-tidycal.b-cdn.net/js/embed.js";

    // Remove any previously-injected TidyCal script + reset the window API
    document
      .querySelectorAll(`script[src="${SCRIPT_SRC}"]`)
      .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    try {
      // Clear cached global so the new script fully re-initialises
      if (window.TidyCal) delete window.TidyCal;
    } catch (e) {
      window.TidyCal = undefined;
    }

    // Inject a fresh script — its own load event will scan the DOM
    // and mount the widget inside our .tidycal-embed div.
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    document.body.appendChild(s);

    return () => {
      // On unmount (SPA nav away), clean up so a future mount is deterministic.
      if (s.parentNode) s.parentNode.removeChild(s);
      try {
        if (window.TidyCal) delete window.TidyCal;
      } catch (e) {
        window.TidyCal = undefined;
      }
    };
  }, []);

  return (
    <div
      data-testid="tidycal-embed"
      className="rounded-2xl border border-[var(--border)] bg-white p-4 md:p-6 shadow-[0_12px_40px_-20px_rgba(11,37,69,0.18)]"
    >
      <div
        className="tidycal-embed"
        data-path="sirdionmagee/business-general-consultation-15-minutes"
      />
    </div>
  );
}

// ------------------------------
// Multi-step Proposal Wizard
// ------------------------------
const STEPS = [
  { id: 1, title: "About you" },
  { id: 2, title: "Event details" },
  { id: 3, title: "Services" },
  { id: 4, title: "Notes & submit" },
];

function ProposalWizard() {
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [prefilled, setPrefilled] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [data, setData] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    phone: "",
    event_type: "",
    attendees: "",
    location: "",
    event_date: "",
    budget: "",
    services: [],
    notes: "",
  });

  // ── Prefill from Fit Finder (Event Simulator) ────────────────────
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("magee_quiz_answers");
      if (!raw) return;
      const q = JSON.parse(raw);
      if (!q || typeof q !== "object") return;

      const notesLine = q.format ? `Format preference: ${q.format}` : "";
      setData((d) => ({
        ...d,
        event_type: d.event_type || q.event_type || "",
        attendees: d.attendees || q.attendees || "",
        budget: d.budget || q.budget || "",
        notes: notesLine && !d.notes ? notesLine : d.notes,
      }));
      // Persist the full quiz signal to forward on submit
      setQuiz({
        event_type: q.event_type || null,
        attendees: q.attendees || null,
        format: q.format || null,
        budget: q.budget || null,
        chapter: q.chapter || null,
        chapter_id: q.chapter_id || null,
      });
      setPrefilled(true);
      sessionStorage.removeItem("magee_quiz_answers");
    } catch (e) {
      // sessionStorage blocked or malformed — safe to ignore
    }
  }, []);

  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const toggleService = (svc) =>
    setData((d) => ({
      ...d,
      services: d.services.includes(svc)
        ? d.services.filter((s) => s !== svc)
        : [...d.services, svc],
    }));

  const canNext = () => {
    if (step === 1) return data.name && data.email;
    if (step === 2) return data.event_type && data.attendees;
    if (step === 3) return true;
    return true;
  };

  const submit = async () => {
    setStatus("loading");
    setError("");
    try {
      const payload = quiz ? { ...data, quiz } : data;
      await api.post("/proposals", payload);
      setStatus("success");
      toast.success("Proposal request received.");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setStatus("error");
      setError(msg);
      toast.error(msg);
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid={PROPOSAL.success}
        className="border border-[var(--text)]/10 bg-[var(--surface)] p-10 md:p-16"
      >
        <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--accent)]">
          ✓ Filed — reference generated
        </span>
        <h3 className="mt-6 font-editorial text-4xl md:text-6xl leading-[0.95] tracking-tighter">
          Thank you. A concierge lead
          <br />
          <em className="text-[var(--accent)]">will reach out shortly.</em>
        </h3>
        <p className="mt-8 text-[var(--text)]/60 max-w-lg">
          Expect a personal reply within one business day with a scoped conversation
          and a preliminary approach outline.
        </p>
      </div>
    );
  }

  return (
    <div data-testid={PROPOSAL.root} id="proposal">
      {/* Prefill banner — surfaces when Fit Finder answers land here */}
      <AnimatePresence>
        {prefilled && (
          <motion.div
            key="prefill-banner"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            data-testid="wizard-prefill-banner"
            className="mb-8 flex items-center justify-between gap-4 rounded-2xl border border-[var(--accent)]/40 bg-[var(--accent)]/10 px-5 py-3"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-sky-600 text-white shadow-md shrink-0">
                <Check size={14} />
              </span>
              <div className="min-w-0">
                <div className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-700">
                  Prefilled from your Fit Finder
                </div>
                <div className="text-[13px] text-[var(--text)]/75 truncate">
                  Event details are set — jump to Step 02 or edit anything below.
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setStep(2)}
              data-testid="wizard-jump-step-2"
              className="hidden sm:inline-flex items-center gap-2 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/70 hover:text-[var(--text)] transition-colors shrink-0"
            >
              Review details <ArrowUpRight size={12} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Progress — mobile: current step only, desktop: full 4-step grid */}
      <div className="mb-10">
        {/* Mobile compact stepper */}
        <div className="md:hidden">
          <div className="grid grid-cols-4 gap-1.5 mb-4">
            {STEPS.map((s) => (
              <div
                key={s.id}
                className={`h-[3px] rounded-full transition-all duration-500 ${
                  s.id <= step ? "bg-[var(--accent)]" : "bg-[var(--text)]/10"
                }`}
              />
            ))}
          </div>
          <div className="flex items-baseline justify-between">
            <div className="flex items-baseline gap-2">
              <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text)]/60">
                Step 0{step}
              </span>
              <span className="text-sm text-[var(--text)]">
                {STEPS.find((s) => s.id === step)?.title}
              </span>
            </div>
            <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text)]/40">
              {step} / {STEPS.length}
            </span>
          </div>
        </div>

        {/* Desktop 4-col stepper */}
        <div className="hidden md:grid grid-cols-4 gap-3">
          {STEPS.map((s) => {
            const active = s.id === step;
            const done = s.id < step;
            return (
              <div key={s.id} className="flex flex-col gap-3">
                <div
                  className={`h-[2px] transition-all duration-500 ${
                    active || done ? "bg-[var(--accent)]" : "bg-[var(--text)]/10"
                  }`}
                />
                <div className="flex items-center gap-2">
                  <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text)]/60">
                    Step 0{s.id}
                  </span>
                  <span
                    className={`text-sm ${
                      active ? "text-[var(--text)]" : "text-[var(--text)]/40"
                    }`}
                  >
                    {s.title}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          data-testid={PROPOSAL.step(step)}
        >
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <label>
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/50">
                  Name*
                </span>
                <input
                  required
                  value={data.name}
                  onChange={set("name")}
                  className="input-underline"
                  data-testid={PROPOSAL.field("name")}
                />
              </label>
              <label>
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/50">
                  Email*
                </span>
                <input
                  required
                  type="email"
                  value={data.email}
                  onChange={set("email")}
                  className="input-underline"
                  data-testid={PROPOSAL.field("email")}
                />
              </label>
              <label>
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/50">
                  Company
                </span>
                <input
                  value={data.company}
                  onChange={set("company")}
                  className="input-underline"
                  data-testid={PROPOSAL.field("company")}
                />
              </label>
              <label>
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/50">
                  Role
                </span>
                <input
                  value={data.role}
                  onChange={set("role")}
                  className="input-underline"
                  data-testid={PROPOSAL.field("role")}
                />
              </label>
              <label className="md:col-span-2">
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/50">
                  Phone
                </span>
                <input
                  value={data.phone}
                  onChange={set("phone")}
                  className="input-underline"
                  data-testid={PROPOSAL.field("phone")}
                />
              </label>
            </div>
          )}

          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <label>
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/50">
                  Event Type*
                </span>
                <input
                  required
                  placeholder="Conference, Gala, Association, Hybrid…"
                  value={data.event_type}
                  onChange={set("event_type")}
                  className="input-underline"
                  data-testid={PROPOSAL.field("event_type")}
                />
              </label>
              <label>
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/50">
                  Expected Attendees*
                </span>
                <input
                  required
                  placeholder="e.g., 250"
                  value={data.attendees}
                  onChange={set("attendees")}
                  className="input-underline"
                  data-testid={PROPOSAL.field("attendees")}
                />
              </label>
              <label>
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/50">
                  Location / City
                </span>
                <input
                  value={data.location}
                  onChange={set("location")}
                  className="input-underline"
                  data-testid={PROPOSAL.field("location")}
                />
              </label>
              <label>
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/50">
                  Event Date / Window
                </span>
                <input
                  placeholder="e.g., Q3 2026"
                  value={data.event_date}
                  onChange={set("event_date")}
                  className="input-underline"
                  data-testid={PROPOSAL.field("event_date")}
                />
              </label>
              <label className="md:col-span-2">
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/50">
                  Budget Range
                </span>
                <input
                  placeholder="Optional — helps us scope properly"
                  value={data.budget}
                  onChange={set("budget")}
                  className="input-underline"
                  data-testid={PROPOSAL.field("budget")}
                />
              </label>
            </div>
          )}

          {step === 3 && (
            <div>
              <p className="text-[var(--text)]/60 mb-8 max-w-lg">
                Pick every service you&apos;re considering — we&apos;ll refine the
                scope together.
              </p>
              <div className="flex flex-wrap gap-3">
                {SERVICE_OPTIONS.map((svc) => {
                  const active = data.services.includes(svc);
                  return (
                    <button
                      type="button"
                      key={svc}
                      onClick={() => toggleService(svc)}
                      data-testid={PROPOSAL.serviceChip(
                        svc.toLowerCase().replace(/[^a-z0-9]+/g, "-")
                      )}
                      className={`px-5 py-3 border font-mono-brand text-[11px] uppercase tracking-[0.22em] transition-all flex items-center gap-2 ${
                        active
                          ? "border-[var(--accent)] bg-[var(--accent)] text-[var(--text)]"
                          : "border-[var(--text)]/20 text-[var(--text)]/70 hover:border-[var(--text)]/50"
                      }`}
                    >
                      {active && <Check size={12} />}
                      {svc}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <label className="block">
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/50">
                  Anything we should know?
                </span>
                <textarea
                  rows={6}
                  value={data.notes}
                  onChange={set("notes")}
                  placeholder="Goals, stakeholders, brand context, previous vendors…"
                  className="input-underline"
                  data-testid={PROPOSAL.field("notes")}
                />
              </label>
              <div className="mt-10 border-t border-[var(--text)]/10 pt-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-[var(--text)]/60">
                <div>
                  <div className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/40 mb-2">
                    Contact
                  </div>
                  <div className="text-[var(--text)]">{data.name || "—"}</div>
                  <div>{data.email || "—"}</div>
                  <div>{data.company || "—"}</div>
                </div>
                <div>
                  <div className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/40 mb-2">
                    Event
                  </div>
                  <div className="text-[var(--text)]">{data.event_type || "—"}</div>
                  <div>{data.attendees ? `${data.attendees} attendees` : "—"}</div>
                  <div>{data.location || "—"}</div>
                  <div>{data.event_date || "—"}</div>
                </div>
                <div>
                  <div className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/40 mb-2">
                    Services
                  </div>
                  <div className="text-[var(--text)]">
                    {data.services.length
                      ? data.services.join(", ")
                      : "—"}
                  </div>
                </div>
              </div>
              {error && (
                <p className="mt-6 text-red-400 font-mono-brand text-sm">{error}</p>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Nav */}
      <div className="mt-12 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1}
          data-testid={PROPOSAL.back}
          className="inline-flex items-center gap-2 font-mono-brand text-[11px] uppercase tracking-[0.22em] text-[var(--text)]/70 hover:text-[var(--text)] disabled:opacity-30"
        >
          <ArrowLeft size={14} /> Back
        </button>

        {step < 4 ? (
          <button
            type="button"
            onClick={() => canNext() && setStep((s) => s + 1)}
            disabled={!canNext()}
            data-testid={PROPOSAL.next}
            className="inline-flex items-center gap-3 border border-[var(--text)]/25 hover:border-[var(--accent)] hover:bg-[var(--accent)] transition-all px-6 py-3 font-mono-brand text-[11px] uppercase tracking-[0.22em] disabled:opacity-50"
          >
            Next Step <ArrowUpRight size={14} />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={status === "loading"}
            data-testid={PROPOSAL.submit}
            className="inline-flex items-center gap-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-60 text-[var(--text)] px-8 py-4 font-mono-brand text-[11px] uppercase tracking-[0.22em] transition-all"
          >
            {status === "loading" ? "Filing…" : "Submit Proposal"}
            <ArrowUpRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

// ------------------------------
// Page
// ------------------------------
export default function Contact() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  return (
    <div className="bg-[var(--bg)] text-[var(--text)]">
      <Seo
        title="Contact & Request a Quote — Start Your Event Conversation"
        description="Tell us about your next meeting, association event, or production. Request a quote or a scoped conversation within one business day. Certified event & meeting concierge partners — global."
        path="/contact"
        keywords="event planning quote, event management RFP, meeting planning consultation, event concierge contact, event agency quote request"
      />
      {/* HEADER */}
      <section className="relative pt-36 md:pt-44 pb-16 md:pb-24 border-b border-[var(--text)]/10 overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-50 pointer-events-none" />
        <div className="brand-bloom brand-bloom-blue w-[500px] h-[500px] -top-20 -right-20 opacity-40" />
        <HexMark
          size={240}
          opacity={0.06}
          className="absolute -left-12 top-28 rotate-[10deg] pointer-events-none hidden md:block"
        />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="flex items-center gap-4 mb-10">
              <span className="w-10 h-px bg-[var(--text)]/40" />
              <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--text)]/60">
                § Contact & Proposal
              </span>
            </div>
            <MaskedReveal
              lines={["Tell us about what", "you are creating?"]}
              className="font-editorial tracking-tighter leading-[0.92] text-[13vw] md:text-[8vw] lg:text-[7vw]"
              stagger={0.1}
            />
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-col justify-end">
            <Reveal delay={0.4}>
              <p className="text-[var(--text)]/70 text-lg leading-relaxed">
                A quick note or a full brief — either works. We reply within one
                business day, always.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROPOSAL WIZARD */}
      <section className="py-24 md:py-32 border-b border-[var(--text)]/10">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <Reveal>
            <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--accent)]">
              Contact Us
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 mb-14 font-editorial text-5xl md:text-7xl leading-[0.95] tracking-tighter">
              Request a Quote.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <ProposalWizard />
          </Reveal>
        </div>
      </section>

      {/* BOOKING — TidyCal 15-min consultation */}
      <section id="book" className="py-24 md:py-32 scroll-mt-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex items-center gap-4">
              {/* Coffee + tea twin motif — gold + cyan */}
              <span className="relative flex items-center shrink-0" aria-hidden="true" data-testid="contact-coffee-motif">
                <span className="relative z-[2] w-11 h-11 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 text-[#1A1204] flex items-center justify-center shadow-[0_10px_24px_-8px_rgba(214,167,72,0.55)] rotate-[-6deg]">
                  <Coffee size={18} strokeWidth={2.4} />
                  <span
                    className="absolute -top-2 left-1/2 -translate-x-1/2 w-[3px] h-3 bg-white/70 rounded-full blur-[2px]"
                    style={{ animation: "coffeeSteamA 3s ease-in-out infinite" }}
                  />
                  <span
                    className="absolute -top-3 left-[calc(50%+4px)] -translate-x-1/2 w-[3px] h-3 bg-white/60 rounded-full blur-[2px]"
                    style={{ animation: "coffeeSteamB 3.4s ease-in-out .3s infinite" }}
                  />
                </span>
                <span className="relative -ml-4 w-11 h-11 rounded-full bg-gradient-to-br from-cyan-300 to-sky-500 text-[#052030] flex items-center justify-center shadow-[0_10px_24px_-8px_rgba(6,182,212,0.55)] rotate-[6deg]">
                  <Coffee size={18} strokeWidth={2.4} />
                </span>
              </span>
              <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--text)]/50">
                Schedule a consultation
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 mb-14 font-editorial text-5xl md:text-7xl leading-[0.95] tracking-tighter">
              Grab a Coffee &amp; Let&rsquo;s Chat!
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <ContactForm />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
